import { router } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useState, useEffect } from 'react'

interface ReceiptData {
  transaction: {
    unique_reference: string
    account_id: number
    date: string
    memo: string
    amount: number
    amount_currency: string
    category_id: number | null
    has_tax: boolean
  }
  receipt_signed_id: string
  categories: { id: number; name: string; kind: string }[]
  preview_url: string
}

interface ReceiptConfirmDialogProps {
  data: ReceiptData | null
  onClose: () => void
}

export function ReceiptConfirmDialog({ data, onClose }: ReceiptConfirmDialogProps) {
  const [form, setForm] = useState(data?.transaction)

  useEffect(() => {
    if (data?.transaction) {
      setForm(data.transaction)
    }
  }, [data])

  if (!data || !form) return null

  function handleClose() {
    if (data?.preview_url) {
      URL.revokeObjectURL(data.preview_url)
    }
    onClose()
  }

  function handleConfirm() {
    router.post('/receipt_transactions', {
      transaction: form,
      receipt_signed_id: data!.receipt_signed_id,
    }, {
      onSuccess: () => handleClose(),
    })
  }

  return (
    <Dialog open={!!data} onOpenChange={() => handleClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-[15px]">Confirm Receipt Transaction</DialogTitle>
        </DialogHeader>

        {data.preview_url && (
          <div className="border rounded-lg overflow-hidden bg-muted">
            <img
              src={data.preview_url}
              alt="Receipt preview"
              className="w-full max-h-56 object-contain"
            />
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label className="text-[13px]">Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[13px]">Memo</Label>
            <Input
              value={form.memo}
              onChange={(e) => setForm({ ...form, memo: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[13px]">Amount</Label>
            <Input
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[13px]">Category</Label>
            <Select
              value={form.category_id?.toString() || '__none__'}
              onValueChange={(value) =>
                setForm({ ...form, category_id: value === '__none__' ? null : Number(value) })
              }
            >
              <SelectTrigger className="mt-1 text-[13px]">
                <SelectValue placeholder="Uncategorized" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Uncategorized</SelectItem>
                {data.categories.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={handleClose}>Cancel</Button>
          <Button size="sm" onClick={handleConfirm}>Create Transaction</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
